import { useState, useEffect } from 'preact/hooks';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Folder,
  FolderOpen,
  File,
  FileText,
  FileCode,
  FileImage,
  FileVideo,
  FileAudio,
  Plus,
  Edit,
  Trash2,
  Upload,
  Download,
  Search,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  Copy,
  Move,
  Eye,
  EyeOff
} from 'lucide-preact';

interface ProjectFile {
  name: string;
  type: 'file' | 'directory';
  path: string;
  size?: number;
  modified?: number;
  children?: ProjectFile[];
}

interface FileExplorerProps {
  projectId?: string;
  onFileSelect: (file: ProjectFile) => void;
  onFileEdit: (file: ProjectFile) => void;
  onFileCreate: (path: string, isDirectory: boolean) => Promise<void>;
  onFileDelete: (fileId: string) => Promise<void>;
  onFileRename: (fileId: string, newPath: string) => Promise<void>;
  onFileMove: (fileId: string, newPath: string) => Promise<void>;
  onFileCopy: (fileId: string, newPath: string) => Promise<void>;
}

interface FileTreeNode {
  id: string;
  name: string;
  path: string;
  isDirectory: boolean;
  children: FileTreeNode[];
  isExpanded: boolean;
  lastModified: number;
  fileType?: string;
}

const FILE_TYPE_ICONS = {
  'js': FileCode,
  'ts': FileCode,
  'tsx': FileCode,
  'jsx': FileCode,
  'py': FileCode,
  'java': FileCode,
  'cpp': FileCode,
  'c': FileCode,
  'cs': FileCode,
  'php': FileCode,
  'rb': FileCode,
  'go': FileCode,
  'rs': FileCode,
  'html': FileCode,
  'css': FileCode,
  'scss': FileCode,
  'sass': FileCode,
  'less': FileCode,
  'json': FileCode,
  'xml': FileCode,
  'yaml': FileCode,
  'yml': FileCode,
  'md': FileText,
  'txt': FileText,
  'log': FileText,
  'csv': FileText,
  'pdf': FileText,
  'doc': FileText,
  'docx': FileText,
  'jpg': FileImage,
  'jpeg': FileImage,
  'png': FileImage,
  'gif': FileImage,
  'svg': FileImage,
  'webp': FileImage,
  'mp4': FileVideo,
  'avi': FileVideo,
  'mov': FileVideo,
  'wmv': FileVideo,
  'mp3': FileAudio,
  'wav': FileAudio,
  'flac': FileAudio,
  'aac': FileAudio,
};

export function FileExplorer({
  projectId,
  onFileSelect,
  onFileEdit,
  onFileCreate,
  onFileDelete,
  onFileRename,
  onFileMove,
  onFileCopy
}: FileExplorerProps) {
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [fileTree, setFileTree] = useState<FileTreeNode[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; file: ProjectFile } | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [showHiddenFiles, setShowHiddenFiles] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadFiles();
    }
  }, [projectId]);

  useEffect(() => {
    buildFileTree();
  }, [files, expandedFolders]);

  const loadFiles = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/files`);
      
      if (response.ok) {
        const data = await response.json();
        setFiles(data);
      } else {
        console.error('Failed to load project files:', response.statusText);
        setFiles([]);
      }
    } catch (error) {
      console.error('Failed to load files:', error);
      setFiles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const buildFileTree = () => {
    const tree: FileTreeNode[] = [];

    // Convert our file structure to tree nodes
    const convertToTreeNodes = (files: ProjectFile[], parentPath = ''): FileTreeNode[] => {
      return files.map(file => {
        const fullPath = parentPath ? `${parentPath}/${file.name}` : file.name;
        const node: FileTreeNode = {
          id: fullPath, // Use path as ID
          name: file.name,
          path: fullPath,
          isDirectory: file.type === 'directory',
          children: file.children ? convertToTreeNodes(file.children, fullPath) : [],
          isExpanded: expandedFolders.has(fullPath),
          lastModified: file.modified || Date.now(),
          fileType: file.type === 'file' ? getFileExtension(file.name) : undefined
        };
        return node;
      });
    };

    // Filter files based on search and hidden files
    const filteredFiles = files.filter(file => {
      const matchesSearch = !searchQuery || file.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesHidden = showHiddenFiles || !file.name.startsWith('.');
      return matchesSearch && matchesHidden;
    });

    tree.push(...convertToTreeNodes(filteredFiles));

    setFileTree(tree);
  };

  const getFileExtension = (filename: string): string => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  };

  const getFileIcon = (fileType: string, isDirectory: boolean) => {
    if (isDirectory) {
      return Folder;
    }
    
    const IconComponent = FILE_TYPE_ICONS[fileType as keyof typeof FILE_TYPE_ICONS];
    return IconComponent || File;
  };

  const handleFileClick = (file: ProjectFile) => {
    setSelectedFile(file.path);
    onFileSelect(file);
  };

  const handleFolderToggle = (path: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const handleContextMenu = (e: MouseEvent, file: ProjectFile) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      file
    });
  };

  const handleCreateFile = async (path: string) => {
    try {
      await onFileCreate(path, false);
      await loadFiles();
    } catch (error) {
      console.error('Failed to create file:', error);
    }
  };

  const handleCreateFolder = async (path: string) => {
    try {
      await onFileCreate(path, true);
      await loadFiles();
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await onFileDelete(fileId);
      await loadFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const renderFileNode = (node: FileTreeNode, depth: number = 0) => {
    const IconComponent = getFileIcon(node.fileType || '', node.isDirectory);
    const isSelected = selectedFile === node.id;
    
    return (
      <div key={node.id} className="select-none">
        <div
          className={`flex items-center gap-2 py-1 px-2 hover:bg-muted/50 cursor-pointer rounded ${
            isSelected ? 'bg-primary/10' : ''
          }`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => {
            if (node.isDirectory) {
              handleFolderToggle(node.path);
            } else {
              // Find the file in our tree structure
              const findFileByPath = (files: ProjectFile[], targetPath: string): ProjectFile | null => {
                for (const file of files) {
                  if (file.path === targetPath) {
                    return file;
                  }
                  if (file.children) {
                    const found = findFileByPath(file.children, targetPath);
                    if (found) return found;
                  }
                }
                return null;
              };
              
              const file = findFileByPath(files, node.path);
              if (file) handleFileClick(file);
            }
          }}
          onContextMenu={(e) => {
            // Find the file in our tree structure
            const findFileByPath = (files: ProjectFile[], targetPath: string): ProjectFile | null => {
              for (const file of files) {
                if (file.path === targetPath) {
                  return file;
                }
                if (file.children) {
                  const found = findFileByPath(file.children, targetPath);
                  if (found) return found;
                }
              }
              return null;
            };
            
            const file = findFileByPath(files, node.path);
            if (file) handleContextMenu(e, file);
          }}
        >
          {node.isDirectory && (
            <div className="w-4 h-4 flex items-center justify-center">
              {node.isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </div>
          )}
          
          <IconComponent className="h-4 w-4 text-muted-foreground" />
          
          <span className="text-sm truncate flex-1">{node.name}</span>
          
          <div className="text-xs text-muted-foreground">
            {new Date(node.lastModified).toLocaleDateString()}
          </div>
        </div>
        
        {node.isDirectory && node.isExpanded && (
          <div>
            {node.children.map(child => renderFileNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderContextMenu = () => {
    if (!contextMenu) return null;

    const { file } = contextMenu;

    return (
      <div
        className="fixed bg-background border rounded-md shadow-lg z-50 py-1 min-w-32"
        style={{
          left: contextMenu.x,
          top: contextMenu.y,
        }}
        onClick={() => setContextMenu(null)}
      >
        <button
          className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
          onClick={() => {
            onFileEdit(file);
            setContextMenu(null);
          }}
        >
          <Edit className="h-4 w-4" />
          Edit
        </button>
        
        <button
          className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
          onClick={() => {
            handleFileCopy(file.id, `${file.path}_copy`);
            setContextMenu(null);
          }}
        >
          <Copy className="h-4 w-4" />
          Copy
        </button>
        
        <button
          className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
          onClick={() => {
            const newName = prompt('Enter new name:', file.path);
            if (newName && newName !== file.path) {
              handleFileRename(file.id, newName);
            }
            setContextMenu(null);
          }}
        >
          <Edit className="h-4 w-4" />
          Rename
        </button>
        
        <Separator className="my-1" />
        
        <button
          className="w-full px-3 py-2 text-left text-sm hover:bg-muted text-destructive flex items-center gap-2"
          onClick={() => {
            if (confirm(`Are you sure you want to delete ${file.path}?`)) {
              handleDeleteFile(file.id);
            }
            setContextMenu(null);
          }}
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <h3 className="text-lg font-semibold">Files</h3>
          <p className="text-sm text-muted-foreground">
            {projectId ? `Project files` : 'Select a project to view files'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHiddenFiles(!showHiddenFiles)}
            title="Toggle hidden files"
          >
            {showHiddenFiles ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={loadFiles}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          
          <Button
            size="sm"
            onClick={() => {
              const fileName = prompt('Enter file name:');
              if (fileName) {
                handleCreateFile(fileName);
              }
            }}
            disabled={!projectId}
          >
            <Plus className="h-4 w-4 mr-2" />
            New File
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="p-4 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onInput={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
            placeholder="Search files..."
            className="pl-10"
          />
        </div>
      </div>

      {/* File Tree */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : fileTree.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
            <Folder className="h-12 w-12 mb-2" />
            <p className="text-sm">No files found</p>
            {projectId && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => {
                  const fileName = prompt('Enter file name:');
                  if (fileName) {
                    handleCreateFile(fileName);
                  }
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create first file
              </Button>
            )}
          </div>
        ) : (
          <div className="p-2">
            {fileTree.map(node => renderFileNode(node))}
          </div>
        )}
      </div>

      {/* File Stats */}
      <div className="p-4 border-t">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {files.filter(f => !f.isDirectory).length} files, {files.filter(f => f.isDirectory).length} folders
          </span>
          <span>
            {searchQuery && `Filtered from ${files.length} total`}
          </span>
        </div>
      </div>

      {/* Context Menu */}
      {renderContextMenu()}
    </div>
  );
}
